/**
 * Step 1 — Basic info.
 *
 * Name, description, segment (24-option UI selector), domain (engine
 * type, 7 values), version, author display name. Uses React Hook
 * Form + Zod.
 *
 * Segment selection is a UI-only affordance: it pre-fills the
 * underlying engine `domain` field (a 7-value enum) via `setValue`.
 * The signed manifest carries `domain` only; segment is not
 * persisted to the store. The wizard step's Zod schema is unchanged
 * — `step1Schema` still validates the seven-value `domain`.
 *
 * Pre-fill via query params: `/spark?starter=<id>&segment=<id>`. The
 * Marketplace Categories tab uses these params when the user clicks
 * a starter card so the wizard opens with the right segment + domain
 * already chosen and a sensible default `name`.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import {
  SEGMENTS,
  getSegment,
  getSegmentForStarter,
  type Segment,
} from '@/data/segments';
import { lookupProductPrefill } from '@/data/productPrefills';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils/cn';

import { DOMAIN_VALUES, step1Schema, type Step1Values } from '../schemas';
import { useSparkStore } from '../store';

export function Step1Basic({ onAdvance }: { onAdvance: () => void }) {
  const { t } = useTranslation(['spark', 'common', 'segments']);
  const draft = useSparkStore();
  const [searchParams] = useSearchParams();
  // Pull the signed-in user's display name so the Author field is
  // pre-filled with something better than the placeholder "you".
  // Falls through to the draft's persisted author when the user
  // isn't signed in (engine-bearer / unauthenticated wizard runs).
  const authUserName = useAuthStore((s) => s.user?.name ?? null);
  const authorPrefill =
    (authUserName && authUserName.trim().length > 0)
      ? authUserName
      : draft.author_name;

  // Resolve any prefill the Marketplace handed us.
  const prefill = useMemo(() => resolvePrefill(searchParams), [searchParams]);

  const form = useForm<Step1Values>({
    resolver: zodResolver(step1Schema),
    mode: 'onBlur',
    defaultValues: {
      name: prefill?.name ?? draft.name,
      description: prefill?.description ?? draft.description,
      version: draft.version,
      domain: (prefill?.segment?.engineDomain ?? draft.domain) as Step1Values['domain'],
      author_name: authorPrefill,
    },
  });

  // Local UI state — which segment is highlighted in the segment
  // selector. Not persisted; the form's `domain` is the only thing
  // committed to the wizard store.
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>(
    prefill?.segment?.id ?? '',
  );

  // If the user navigated in with `?starter=...` or `?product=...`
  // after the wizard had a partial draft, push the prefill into the
  // form on mount. setValue is the right tool here: useForm's
  // defaultValues only run once at construction time, so a
  // late-arriving prefill (Marketplace navigation, route change) needs
  // to write directly to the form's controlled values.
  //
  // ALL five visible fields land here when the prefill resolves —
  // name, description, domain, segment selector, author. The earlier
  // version filled name + segment only and looked half-broken to
  // anyone landing on the page from a Marketplace card.
  useEffect(() => {
    if (!prefill) return;
    if (prefill.name) form.setValue('name', prefill.name, { shouldValidate: false });
    if (prefill.description) {
      form.setValue('description', prefill.description, { shouldValidate: false });
    }
    if (prefill.segment) {
      form.setValue('domain', prefill.segment.engineDomain as Step1Values['domain'], {
        shouldValidate: false,
      });
      setSelectedSegmentId(prefill.segment.id);
    }
    // Author always gets the freshest signed-in identity. If the
    // store still holds the placeholder "you" but we know the user's
    // real name from auth, push the real name in. Without this, a
    // user landing on /spark?product=… via Marketplace sees "you" in
    // the Author field even though they're signed in — confusing.
    if (authorPrefill && form.getValues('author_name') !== authorPrefill) {
      form.setValue('author_name', authorPrefill, { shouldValidate: false });
    }
    // Run only when prefill changes (effectively once on mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, authorPrefill]);

  const onSegmentChange = (id: string) => {
    setSelectedSegmentId(id);
    if (!id) return;
    const seg = getSegment(id);
    if (!seg) return;
    form.setValue('domain', seg.engineDomain as Step1Values['domain'], {
      shouldValidate: true,
    });
  };

  const onSubmit = (v: Step1Values) => {
    useSparkStore.getState().applyStep1(v);
    onAdvance();
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      <Field
        label={t('steps.1.name_label')}
        hint={t('steps.1.name_hint')}
        error={form.formState.errors.name?.message}
      >
        <input
          type="text"
          autoFocus
          autoComplete="off"
          placeholder={t('steps.1.name_placeholder')}
          {...form.register('name')}
          className={inputClass(Boolean(form.formState.errors.name))}
        />
      </Field>

      <Field
        label={t('steps.1.description_label')}
        hint={t('steps.1.description_hint')}
        error={form.formState.errors.description?.message}
      >
        <textarea
          rows={3}
          {...form.register('description')}
          placeholder={t('steps.1.description_placeholder')}
          className={inputClass(Boolean(form.formState.errors.description))}
        />
      </Field>

      <Field
        label={t('steps.1.segment_label', { defaultValue: 'Segment (optional)' })}
        hint={t('steps.1.segment_hint', {
          defaultValue:
            'Pick a category to set the engine domain automatically. Leave blank for a generic GI.',
        })}
      >
        <select
          value={selectedSegmentId}
          onChange={(e) => onSegmentChange(e.target.value)}
          className={inputClass(false)}
          data-testid="step1-segment-select"
          aria-label={t('steps.1.segment_label', { defaultValue: 'Segment (optional)' })}
        >
          <option value="">{t('steps.1.segment_placeholder', { defaultValue: '— none —' })}</option>
          <SegmentOptgroups segments={SEGMENTS} />
        </select>
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label={t('steps.1.domain_label')}
          hint={t('steps.1.domain_hint')}
          error={form.formState.errors.domain?.message}
        >
          <select
            {...form.register('domain')}
            className={inputClass(Boolean(form.formState.errors.domain))}
          >
            {DOMAIN_VALUES.map((d) => (
              <option key={d} value={d}>
                {t(`domain.${d}`, { ns: 'common' })}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t('steps.1.version_label')}
          hint={t('steps.1.version_hint')}
          error={form.formState.errors.version?.message}
        >
          <input
            type="text"
            inputMode="decimal"
            {...form.register('version')}
            className={cn(inputClass(Boolean(form.formState.errors.version)), 'font-mono')}
          />
        </Field>
      </div>

      <Field
        label={t('steps.1.author_label')}
        hint={t('steps.1.author_hint')}
        error={form.formState.errors.author_name?.message}
      >
        <input
          type="text"
          {...form.register('author_name')}
          className={inputClass(Boolean(form.formState.errors.author_name))}
        />
      </Field>

      <WizardFooter>
        <Button type="submit" variant="primary">
          {t('actions.next', { ns: 'common' })}
        </Button>
      </WizardFooter>
    </form>
  );
}

/**
 * Resolves Spark prefill from query params. Three sources, checked
 * in this order:
 *
 *   - `?product=<slug>` — Marketplace / website "Use this product"
 *     CTA. Looks up the slug in PRODUCT_PREFILLS and returns the
 *     product's name + description + the segment matching its
 *     `segmentId`. Fills all three fields.
 *   - `?starter=<id>` — segment-card starter recipes (65 across the
 *     13 ready segments). Returns the parent segment and a sluggified
 *     starter name; description stays empty (the i18n oneliner lives
 *     in starterPrefills, but resolvePrefill keeps the lighter shape
 *     here — Step1Basic's setValue is what actually flows through to
 *     the form).
 *   - `?segment=<id>` alone — segment selected without a starter.
 *
 * Returns `{ segment?, name?, description? }` so the caller's
 * setValue chain only writes the fields that are present.
 */
function resolvePrefill(
  searchParams: URLSearchParams,
):
  | {
      segment: Segment | undefined;
      name: string | undefined;
      description: string | undefined;
    }
  | undefined {
  const productParam = searchParams.get('product');
  const starterParam = searchParams.get('starter');
  const segmentParam = searchParams.get('segment');
  if (!productParam && !starterParam && !segmentParam) return undefined;

  let segment: Segment | undefined;
  let name: string | undefined;
  let description: string | undefined;

  // Source 1: product slug. Resolves to a name + description + a
  // canonical segment via the prefill registry's `segmentId`.
  // We try three forms in case the URL ever drifts from the data:
  // (a) the URL form as-is (today's canonical form is kebab —
  // `citizen-mirror`), (b) underscore-normalized (`citizen_mirror`),
  // (c) hyphen-normalized (`citizen-mirror`). The first hit wins.
  if (productParam) {
    const product =
      lookupProductPrefill(productParam) ??
      lookupProductPrefill(productParam.replace(/-/g, '_')) ??
      lookupProductPrefill(productParam.replace(/_/g, '-'));
    if (product) {
      name = product.name;
      description = product.description;
      segment = getSegment(product.segmentId);
    }
  }

  // Source 2: starter id. Walks all segments to find the owner; the
  // sluggified starter id becomes a default name (the actual title
  // lives in i18n, but the name field accepts the kebab form). Same
  // both-forms lookup so kebab URLs (`achievement-proof`) and the
  // segment-data underscore form (`achievement_proof`) both resolve.
  if (!segment && starterParam) {
    const found =
      getSegmentForStarter(starterParam) ??
      getSegmentForStarter(starterParam.replace(/-/g, '_')) ??
      getSegmentForStarter(starterParam.replace(/_/g, '-'));
    if (found) {
      segment = found.segment;
      if (!name) {
        // Render the name in kebab form for the input regex
        // /^[a-z0-9][a-z0-9_-]*$/i.
        name = starterParam.replace(/_/g, '-');
      }
    }
  }

  // Source 3: bare segment id, no starter / product attached. Try
  // both forms here too — segment ids are kebab in segments.ts but
  // a stray underscore from another window's URL builder wouldn't
  // match without normalization.
  if (!segment && segmentParam) {
    segment =
      getSegment(segmentParam) ??
      getSegment(segmentParam.replace(/_/g, '-')) ??
      getSegment(segmentParam.replace(/-/g, '_'));
  }

  return { segment, name, description };
}

/** Segment options grouped by tier so the user can scan all 24 in
 *  one dropdown without losing context. Ready and Coming-Soon are
 *  visually annotated. */
function SegmentOptgroups({ segments }: { segments: readonly Segment[] }) {
  const { t } = useTranslation('segments');
  const byTier = (tier: 1 | 2 | 3) => segments.filter((s) => s.tier === tier);
  const renderOption = (s: Segment) => {
    const ready = s.status === 'ready';
    return (
      <option key={s.id} value={s.id}>
        {t(`segments.${s.id}.name`)}
        {ready ? '' : ` (${t('status.coming_soon')})`}
      </option>
    );
  };
  return (
    <>
      <optgroup label={t('tier.t1_label')}>{byTier(1).map(renderOption)}</optgroup>
      <optgroup label={t('tier.t2_label')}>{byTier(2).map(renderOption)}</optgroup>
      <optgroup label={t('tier.t3_label')}>{byTier(3).map(renderOption)}</optgroup>
    </>
  );
}

// ─── Shared within this file & siblings ──────────────────────────

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      ) : hint ? (
        <span className="text-xs text-ink-muted">{hint}</span>
      ) : null}
    </label>
  );
}

export function inputClass(hasError: boolean): string {
  return cn(
    'rounded-md border bg-surface px-3 py-2 text-sm text-ink',
    'placeholder:text-ink-subtle',
    'transition-colors',
    hasError
      ? 'border-danger focus-visible:border-danger'
      : 'border-border focus-visible:border-spark',
  );
}

export function WizardFooter({
  children,
  onBack,
}: {
  children: React.ReactNode;
  onBack?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-4">
      <div>
        {onBack ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            {t('actions.back')}
          </Button>
        ) : null}
      </div>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}
